import { useMemo, useState, type ReactNode } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import {
  HiOutlineClock,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineLink,
  HiOutlineOfficeBuilding,
  HiOutlineSparkles,
  HiOutlineUsers,
  HiOutlineViewList,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PageLayout, StandardTable, TableSkeleton } from "@components";
import { Inline, PageHeader, Panel, Stack, Text } from "@design-system";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAdminDashboard } from "../../hooks";
import {
  AdminAttentionItem,
  AdminWorkspaceSummary,
} from "../../api/adminDashboard";
import { ITableRow, IWorkspaceAccess, WorkspaceRole } from "../../types";
import {
  getCustomerAccountDetails,
  getCustomerAccountName,
} from "./workspacePresentation";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LineElement,
  LinearScale,
  Legend,
  PointElement,
  Tooltip,
);

const CHART_COLORS = [
  "#6d28d9",
  "#0ea5e9",
  "#14b8a6",
  "#f59e0b",
  "#ef4444",
  "#64748b",
];

const formatDate = (value: string | null, locale: string) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

interface MetricCardProps {
  label: string;
  value: number;
  helper?: string;
  icon: ReactNode;
  warning?: boolean;
}

const MetricCard = ({
  label,
  value,
  helper,
  icon,
  warning = false,
}: MetricCardProps) => (
  <Panel variant="compact" className="p-4 min-w-0">
    <Inline justify="between" align="start" gap={3}>
      <Stack gap={1} className="min-w-0">
        <Text
          variant="muted"
          className="text-xs font-medium uppercase tracking-wide"
        >
          {label}
        </Text>
        <Text
          as="p"
          className={`text-2xl font-semibold ${warning && value > 0 ? "text-danger" : "text-text"}`}
        >
          {value.toLocaleString()}
        </Text>
        {helper ? (
          <Text variant="secondary" className="text-xs">
            {helper}
          </Text>
        ) : null}
      </Stack>
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          warning && value > 0
            ? "bg-red-50 text-danger dark:bg-red-950/30"
            : "bg-brand-muted text-brand"
        }`}
      >
        {icon}
      </span>
    </Inline>
  </Panel>
);

const AttentionRow = ({
  item,
  locale,
}: {
  item: AdminAttentionItem;
  locale: string;
}) => (
  <div className="flex items-start gap-3 border-b border-border px-4 py-3 last:border-b-0">
    <span
      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
        item.severity === "error" ? "bg-red-500" : "bg-amber-500"
      }`}
    />
    <div className="min-w-0 flex-1">
      <Text className="text-sm font-medium">{item.title}</Text>
      <Text variant="secondary" className="mt-0.5 text-xs break-words">
        {[item.workspaceName, item.detail].filter(Boolean).join(" · ")}
      </Text>
    </div>
    {item.occurredAt ? (
      <Text variant="muted" className="shrink-0 text-xs">
        {formatDate(item.occurredAt, locale)}
      </Text>
    ) : null}
  </div>
);

export const AdminDashboardPage = () => {
  const [periodDays, setPeriodDays] = useState(30);
  const { t, i18n } = useTranslation(["administration", "navigation"]);
  const locale = i18n.language === "no" ? "nb-NO" : "en-GB";
  const dashboardQuery = useAdminDashboard(periodDays);
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const dashboard = dashboardQuery.data;

  const orderChart = useMemo(() => {
    const counts = dashboard?.orderStatusCounts ?? {};
    const statuses = Object.keys(counts).filter((status) => counts[status] > 0);
    return {
      labels: statuses.map((status) =>
        t(`administration:order_status.${status}`, status),
      ),
      datasets: [
        {
          data: statuses.map((status) => counts[status]),
          backgroundColor: CHART_COLORS,
          borderRadius: 6,
          maxBarThickness: 44,
        },
      ],
    };
  }, [dashboard?.orderStatusCounts, t]);

  const settlementChart = useMemo(() => {
    const counts = dashboard?.settlementStatusCounts ?? {};
    const statuses = Object.keys(counts).filter((status) => counts[status] > 0);
    return {
      labels: statuses.map((status) =>
        t(`administration:settlement_status.${status}`, status),
      ),
      datasets: [
        {
          data: statuses.map((status) => counts[status]),
          backgroundColor: CHART_COLORS,
          borderWidth: 0,
        },
      ],
    };
  }, [dashboard?.settlementStatusCounts, t]);

  const dailyActivityChart = useMemo(
    () => ({
      labels: (dashboard?.dailyOrderActivity ?? []).map((item) =>
        new Intl.DateTimeFormat(locale, {
          day: "numeric",
          month: "short",
        }).format(new Date(`${item.date}T12:00:00`)),
      ),
      datasets: [
        {
          label: t("administration:charts.orders"),
          data: (dashboard?.dailyOrderActivity ?? []).map(
            (item) => item.orderCount,
          ),
          borderColor: "#6d28d9",
          backgroundColor: "rgba(109, 40, 217, 0.12)",
          pointBackgroundColor: "#6d28d9",
          pointRadius: periodDays <= 30 ? 3 : 0,
          pointHoverRadius: 5,
          borderWidth: 2,
          tension: 0.28,
          fill: true,
        },
      ],
    }),
    [dashboard?.dailyOrderActivity, locale, periodDays, t],
  );

  const allCompanyVolume = useMemo(
    () =>
      (dashboard?.orderVolumeByCompany ?? [])
        .filter((item) => item.orderCount > 0)
        .map((item) => ({
          ...item,
          companyName: item.companyId
            ? item.companyName
            : t("administration:charts.unattributed_company"),
        })),
    [dashboard?.orderVolumeByCompany, t],
  );

  const companyVolume = useMemo(
    () => allCompanyVolume.slice(0, 10),
    [allCompanyVolume],
  );

  const companyVolumeChart = useMemo(
    () => ({
      labels: companyVolume.map((item) => item.companyName),
      datasets: [
        {
          label: t("administration:charts.orders"),
          data: companyVolume.map((item) => item.orderCount),
          backgroundColor: "#6d28d9",
          borderRadius: 6,
          maxBarThickness: 44,
        },
      ],
    }),
    [companyVolume, t],
  );

  const clientDistributionChart = useMemo(() => {
    const leading = allCompanyVolume.slice(0, 5);
    const remaining = allCompanyVolume
      .slice(5)
      .reduce((total, item) => total + item.orderCount, 0);
    const labels = leading.map((item) => item.companyName);
    const values = leading.map((item) => item.orderCount);
    if (remaining > 0) {
      labels.push(t("administration:charts.other_companies"));
      values.push(remaining);
    }
    return {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: CHART_COLORS,
          borderWidth: 0,
        },
      ],
    };
  }, [allCompanyVolume, t]);

  const recentWorkspaceRows: ITableRow[] = useMemo(
    () =>
      (dashboard?.recentWorkspaces ?? []).map((workspace) => ({
        id: workspace.id,
        isActive: workspace.id === activeWorkspace?.workspaceId,
        cells: [
          {
            id: "name",
            children: (
              <div className="min-w-0">
                <div className="truncate font-medium text-text">
                  {getCustomerAccountName(workspace)}
                </div>
                <div className="truncate text-xs text-secondary">
                  {getCustomerAccountDetails(workspace)}
                </div>
              </div>
            ),
            showOnSmallScreen: true,
          },
          { id: "companyCount", children: workspace.companyCount },
          { id: "ordersInPeriod", children: workspace.ordersInPeriod },
          {
            id: "lastActivityAt",
            children: formatDate(workspace.lastActivityAt, locale),
          },
        ],
        onRowClickData: workspace,
      })),
    [activeWorkspace?.workspaceId, dashboard?.recentWorkspaces, locale],
  );

  const openWorkspace = (workspace: AdminWorkspaceSummary) => {
    const access: IWorkspaceAccess = {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
      name: workspace.owner,
      email: workspace.owner,
      role: WorkspaceRole.Owner,
      isApiKey: false,
      createdBy: workspace.owner,
    };
    setActiveWorkspace(access);
    navigate("/dashboard");
  };

  if (dashboardQuery.isLoading) {
    return (
      <PageLayout>
        <PageHeader
          title={t("administration:title")}
          description={t("administration:description")}
          className="mb-6"
        />
        <TableSkeleton />
      </PageLayout>
    );
  }

  if (dashboardQuery.isError || !dashboard) {
    return (
      <PageLayout>
        <PageHeader
          title={t("administration:title")}
          description={t("administration:description")}
          className="mb-6"
        />
        <Panel className="p-6">
          <Text className="font-medium text-danger">
            {t("administration:load_error")}
          </Text>
        </Panel>
      </PageLayout>
    );
  }

  const summary = dashboard.summary;
  const hasOrderData = orderChart.labels.length > 0;
  const hasSettlementData = settlementChart.labels.length > 0;
  const hasDailyActivity = dailyActivityChart.datasets[0].data.some(
    (value) => value > 0,
  );
  const hasCompanyVolume = companyVolume.length > 0;

  return (
    <PageLayout>
      <Stack gap={6}>
        <Inline justify="between" align="end" wrap gap={4}>
          <PageHeader
            title={t("administration:title")}
            description={t("administration:description")}
          />
          <label className="flex flex-col gap-1 text-sm text-secondary">
            {t("administration:period.label")}
            <select
              className="min-w-36 rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text focus:border-brand focus:ring-brand"
              value={periodDays}
              onChange={(event) => setPeriodDays(Number(event.target.value))}
            >
              {[7, 30, 90].map((days) => (
                <option key={days} value={days}>
                  {t("administration:period.days", { count: days })}
                </option>
              ))}
            </select>
          </label>
        </Inline>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label={t("administration:metrics.workspaces")}
            value={summary.totalWorkspaces}
            helper={t("administration:metrics.active_30d", {
              count: summary.activeWorkspaces30d,
            })}
            icon={<HiOutlineOfficeBuilding className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.active_7d")}
            value={summary.activeWorkspaces7d}
            helper={t("administration:metrics.new_in_period", {
              count: summary.newWorkspacesInPeriod,
              days: periodDays,
            })}
            icon={<HiOutlineClock className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.orders", { days: periodDays })}
            value={summary.ordersInPeriod}
            icon={<HiOutlineViewList className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.invoices", { days: periodDays })}
            value={summary.invoicesInPeriod}
            icon={<HiOutlineDocumentText className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.users")}
            value={summary.humanUsers}
            helper={t("administration:metrics.unverified", {
              count: summary.unverifiedUsers,
            })}
            icon={<HiOutlineUsers className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.accounting_integrations")}
            value={summary.connectedAccountingIntegrations}
            icon={<HiOutlineLink className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.mcp_access")}
            value={summary.activeMcpTokens}
            icon={<HiOutlineSparkles className="h-5 w-5" />}
          />
          <MetricCard
            label={t("administration:metrics.attention")}
            value={summary.attentionCount}
            warning
            icon={<HiOutlineExclamationCircle className="h-5 w-5" />}
          />
        </div>

        <Panel className="p-4">
          <Text as="h2" variant="section-title">
            {t("administration:charts.activity_over_time")}
          </Text>
          <Text variant="secondary" className="mb-4 mt-1 text-sm">
            {t("administration:charts.activity_over_time_description", {
              days: periodDays,
            })}
          </Text>
          <div className="h-72">
            {hasDailyActivity ? (
              <Line
                data={dailyActivityChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { intersect: false, mode: "index" },
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: {
                        color: "#64748b",
                        maxTicksLimit: periodDays <= 30 ? 10 : 12,
                      },
                    },
                    y: {
                      beginAtZero: true,
                      ticks: { precision: 0, color: "#64748b" },
                      grid: { color: "rgba(148, 163, 184, 0.18)" },
                    },
                  },
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                {t("administration:charts.no_data")}
              </div>
            )}
          </div>
        </Panel>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.5fr)]">
          <Panel className="p-4">
            <Text as="h2" variant="section-title">
              {t("administration:charts.orders_by_company")}
            </Text>
            <Text variant="secondary" className="mb-4 mt-1 text-sm">
              {t("administration:charts.orders_by_company_description", {
                days: periodDays,
              })}
            </Text>
            <div className="h-72">
              {hasCompanyVolume ? (
                <Bar
                  data={companyVolumeChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: {
                          color: "#64748b",
                          maxRotation: 35,
                          minRotation: 0,
                        },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: { precision: 0, color: "#64748b" },
                        grid: { color: "rgba(148, 163, 184, 0.18)" },
                      },
                    },
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted">
                  {t("administration:charts.no_company_data")}
                </div>
              )}
            </div>
          </Panel>

          <Panel className="p-4">
            <Text as="h2" variant="section-title">
              {t("administration:charts.client_distribution")}
            </Text>
            <Text variant="secondary" className="mb-4 mt-1 text-sm">
              {t("administration:charts.client_distribution_description")}
            </Text>
            <div className="h-72">
              {hasCompanyVolume ? (
                <Doughnut
                  data={clientDistributionChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "66%",
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          color: "#64748b",
                          boxWidth: 8,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted">
                  {t("administration:charts.no_company_data")}
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <Panel className="p-4">
            <Text as="h2" variant="section-title" className="mb-4">
              {t("administration:charts.orders_by_status")}
            </Text>
            <div className="h-64">
              {hasOrderData ? (
                <Bar
                  data={orderChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      x: {
                        grid: { display: false },
                        ticks: { color: "#64748b" },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: { precision: 0, color: "#64748b" },
                        grid: { color: "rgba(148, 163, 184, 0.18)" },
                      },
                    },
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted">
                  {t("administration:charts.no_data")}
                </div>
              )}
            </div>
          </Panel>

          <Panel className="p-4">
            <Text as="h2" variant="section-title" className="mb-4">
              {t("administration:charts.invoice_settlements")}
            </Text>
            <div className="h-64">
              {hasSettlementData ? (
                <Doughnut
                  data={settlementChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "68%",
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { usePointStyle: true, color: "#64748b" },
                      },
                    },
                  }}
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted">
                  {t("administration:charts.no_settlement_data")}
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <Panel className="overflow-hidden">
            <div className="border-b border-border px-4 py-3">
              <Text as="h2" variant="section-title">
                {t("administration:recent_workspaces.title")}
              </Text>
              <Text variant="secondary" className="mt-1 text-sm">
                {t("administration:recent_workspaces.description")}
              </Text>
            </div>
            <StandardTable
              compact
              canMultiSelect={false}
              header={[
                {
                  id: "name",
                  children: t(
                    "administration:recent_workspaces.customer_account",
                  ),
                  showOnSmallScreen: true,
                },
                {
                  id: "companyCount",
                  children: t("administration:recent_workspaces.companies"),
                },
                {
                  id: "ordersInPeriod",
                  children: t("administration:recent_workspaces.orders", {
                    days: periodDays,
                  }),
                },
                {
                  id: "lastActivityAt",
                  children: t("administration:recent_workspaces.last_activity"),
                },
              ]}
              rows={recentWorkspaceRows}
              onRowClick={openWorkspace}
            />
          </Panel>

          <Panel className="overflow-hidden">
            <div className="border-b border-border px-4 py-3">
              <Text as="h2" variant="section-title">
                {t("administration:attention.title")}
              </Text>
              <Text variant="secondary" className="mt-1 text-sm">
                {t("administration:attention.description")}
              </Text>
            </div>
            {dashboard.attentionItems.length ? (
              dashboard.attentionItems.map((item, index) => (
                <AttentionRow
                  key={`${item.type}-${item.workspaceId ?? "global"}-${index}`}
                  item={item}
                  locale={locale}
                />
              ))
            ) : (
              <div className="px-4 py-8 text-center">
                <Text className="text-sm font-medium">
                  {t("administration:attention.empty_title")}
                </Text>
                <Text variant="secondary" className="mt-1 text-sm">
                  {t("administration:attention.empty_description")}
                </Text>
              </div>
            )}
          </Panel>
        </div>

        <Text variant="muted" className="text-right text-xs">
          {t("administration:generated_at", {
            date: new Intl.DateTimeFormat(locale, {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            }).format(new Date(dashboard.generatedAt)),
          })}
        </Text>
      </Stack>
    </PageLayout>
  );
};
